import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  CanceledError,
  InternalAxiosRequestConfig,
  isAxiosError,
} from "axios"
import { LINKS } from "src/env/links"
import { useApiAbortStore } from "src/stores/apiAbortStore"
import { useApiCallStore } from "src/stores/apiCallStore"
import { useAuthStore } from "src/stores/authStore"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { AuthURL } from "src/util/enums/urls"
import {
  authenticationErrorDialog,
  showHTTP400Dialog,
  showHTTP401Dialog,
  showHTTP403Dialog,
  showHTTP408Dialog,
  showHTTP500Dialog,
  showHTTP501Dialog,
  showHTTP502Dialog,
  showHTTP503Dialog,
  showHTTP504Dialog,
} from "src/util/functions/dialogs"
import { UserPermissionAccess, UserPermissionType } from "src/util/types/dataModels/user"

export type CustomHTTPAxiosConfig = {
  sfxAbortId: string
  multiRequest: boolean
  permissionType?: UserPermissionType
  accessLevel?: UserPermissionAccess
}

declare module "axios" {
  export interface AxiosRequestConfig {
    customConfig?: CustomHTTPAxiosConfig
  }
}

// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside the
// "export default () => {}" function below (which runs individually
// for each client)

const authAPI = axios.create({
  baseURL: LINKS.SPRINGBOARD_URL,
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
})

const springboardAPI = axios.create({
  baseURL: LINKS.SPRINGBOARD_URL,
  headers: { "Content-Type": "application/json" },
})

const csiAPI = axios.create({
  headers: { "Content-Type": "application/json" },
})

let refreshTokenPromise: Promise<string> | null

//region Requests

springboardAPI.interceptors.request.use(requestSuccessHandler, requestErrorHandler)
csiAPI.interceptors.request.use(requestSuccessHandler, requestErrorHandler)

//endregion

//region Responses

authAPI.interceptors.response.use(
  function (response) {
    // Any status code that lies within the range of 2xx causes this function to trigger
    updateLastUserAPICall()

    return response
  },
  async function (error) {
    // Any status codes that fall outside the range of 2xx cause this function to trigger
    if (error.response && error.config.url !== AuthURL.AUTH_REQUEST_ACCESS_TOKEN) {
      switch (error.response.status) {
        case 401:
          return handleErrorUnauthorized(error)
        case 400:
        case 403:
        case 500:
        case 501:
        case 502:
        case 503:
        case 504:
          // This dialog is only temporary. We should update it as soon as we have the submit queue up and running.
          return await authenticationErrorDialog()
      }
    }

    throw error
  }
)

springboardAPI.interceptors.response.use(
  function (response) {
    // Any status code that lies within the range of 2xx causes this function to trigger

    handleAbortRequests(response)

    updateLastUserAPICall()
    updateLastEntityAPICall(response, true)

    return response
  },

  responseErrorHandler
)

csiAPI.interceptors.response.use(
  function (response) {
    // Any status code that lies within the range of 2xx causes this function to trigger

    handleAbortRequests(response)

    updateLastUserAPICall()
    updateLastWorkspaceAPICall(response)

    // We manually set this in the concurrent function
    if (!response.config.customConfig?.multiRequest) {
      updateLastEntityAPICall(response)
    }

    return response
  },

  responseErrorHandler
)

//endregion

//region Helpers

const getRefreshedToken = async (): Promise<string> => {
  const authStore = useAuthStore()
  await authStore.refreshAccessToken()
  return authStore.getAccessTokenString
}

// We need to fetch the token in a wrapper function in order to ensure that the data is always newly fetched.
// If we use this code directly, then the result would be cached and could cause issues.
function getAccessToken() {
  const authStore = useAuthStore()
  return authStore.getAccessTokenString
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
async function handleErrorUnauthorized(error: any) {
  try {
    if (!refreshTokenPromise) {
      refreshTokenPromise = getRefreshedToken()
    }

    const token = await refreshTokenPromise
    const config: AxiosRequestConfig = error.config
    if (config.headers) config.headers.Authorization = token
    // retry the initially failed API request again
    return axios(config)
  } catch {
    Log.debug({
      message: "There was an error while trying to refresh the token.",
      data: { error },
    })

    await showHTTP401Dialog()

    throw new Error("An error has occurred while trying to authenticate the user.")
  } finally {
    refreshTokenPromise = null
  }
}

async function retryRequest(error: AxiosError) {
  if (!error.config) throw error

  const config: AxiosRequestConfig = error.config
  // retry the initially failed API request again
  return axios(config)
}

function requestSuccessHandler(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  config.headers.Authorization = getAccessToken()
  return config
}

function requestErrorHandler(error: unknown): Promise<never> {
  if (error instanceof CanceledError) {
    // User canceled the request, so remove it from the abort store
    const apiAbortStore = useApiAbortStore()
    if (error.config && error.config.customConfig?.sfxAbortId) {
      apiAbortStore.removeController(error.config.customConfig?.sfxAbortId)
    }
  }

  return Promise.reject(error)
}

async function responseErrorHandler(error: unknown) {
  // Any status codes that falls outside the range of 2xx causes this function to trigger

  if (!isAxiosError(error)) throw error

  // In theory, those dialog are persistent, so they will always have return value of TRUE.
  // This is just a bit of future proofing.
  switch (error.response?.status) {
    case 400:
      await showHTTP400Dialog()
      break

    case 401:
      // We want to try to first reauthenticate the user and only on fail show the dialog.
      // This function handles that.
      return handleErrorUnauthorized(error)

    case 403:
      await showHTTP403Dialog()
      break

    case 408: {
      const retry = await showHTTP408Dialog()
      if (retry) return retryRequest(error)
      break
    }

    case 500: {
      const retry = await showHTTP500Dialog()
      if (retry) return retryRequest(error)
      break
    }

    case 501:
      await showHTTP501Dialog()
      break

    case 502: {
      const retry = await showHTTP502Dialog()
      if (retry) return retryRequest(error)
      break
    }

    case 503: {
      const retry = await showHTTP503Dialog()
      if (retry) return retryRequest(error)
      break
    }

    case 504: {
      const retry = await showHTTP504Dialog()
      if (retry) return retryRequest(error)
      break
    }
  }

  throw error
}

/**
 * Dedicated for last global API call for the user.
 */
function updateLastUserAPICall() {
  const userStore = useUserStore()
  // Dedicated for last successful call since last api store is not persistent
  userStore.latestAPICall = Date.now()
}

/**
 * Dedicated for last API call per workspace, so we can detect last interaction.
 *
 * @param response
 */
function updateLastWorkspaceAPICall(response: AxiosResponse) {
  const apiCallStore = useApiCallStore()
  apiCallStore.updateWorkspaceLastApiCallTime(response.config.baseURL)
}

/**
 * Dedicated for last API call per entity, so that we don't have to fetch the same data too often.
 * @param response
 * @param isSpringboardCall
 */
function updateLastEntityAPICall(response: AxiosResponse, isSpringboardCall = false) {
  const apiCallStore = useApiCallStore()
  apiCallStore.updateLastApiCallTime(response.config, isSpringboardCall)
}

function handleAbortRequests(response: AxiosResponse) {
  const apiAbortStore = useApiAbortStore()
  if (response.config.customConfig?.sfxAbortId && !response.config.customConfig?.multiRequest) {
    // Only remove if a singular request, multi requests need to be handled manually
    apiAbortStore.removeController(response.config.customConfig?.sfxAbortId)
  }
}

//endregion

export { authAPI, springboardAPI, csiAPI }
